import React, { useCallback, useState } from "react"
import { Select, Modal, Input, Popconfirm } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFloppyDisk, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useExportMapStore } from "stores/exportMapStore"
import { useExportMap } from "features/QueryConsole/api/useExportMap"
import { useProjectStore } from "stores/projectStore"
import { ExportMapProfile } from "src/types/exportMap.types"
import {
  parseExportMapCSV,
  groupVariables,
  exportMapToCSV,
} from "./utils/exportMapParser"

/**
 * Profile selector component for managing saved export map profiles.
 * Allows loading, saving, and deleting profiles.
 */
const ExportMapProfileSelector: React.FC = () => {
  const { project } = useProjectStore()
  const {
    getProfile,
    saveProfile,
    deleteProfile,
    isLoadingProfile,
    isSavingProfile,
    isDeletingProfile,
  } = useExportMap()

  const {
    availableProfiles,
    selectedProfile,
    setSelectedProfile,
    setAvailableProfiles,
    allVariables,
    setVariables,
    setGroups,
    setRootVariables,
    setHasChanges,
  } = useExportMapStore()

  const [saveAsModalOpen, setSaveAsModalOpen] = useState(false)
  const [newProfileName, setNewProfileName] = useState("")

  /**
   * Handle profile selection change
   */
  const handleProfileChange = useCallback(
    async (profileName: string) => {
      if (!profileName) {
        setSelectedProfile(null)
        return
      }

      try {
        const response = await getProfile(project.id, profileName)
        const profileCsv = response.data
        const profileVariables = parseExportMapCSV(profileCsv)

        // Merge profile codes with current variables
        const updatedVariables = allVariables.map((v) => {
          const profileVar = profileVariables.find(
            (pv) => pv.fullLabel === v.fullLabel
          )
          if (profileVar) {
            return {
              ...v,
              variableCode: profileVar.variableCode,
              categories: v.categories?.map((c) => {
                const profileCat = profileVar.categories?.find(
                  (pc) => pc.label === c.label
                )
                return profileCat ? { ...c, code: profileCat.code } : c
              }),
            }
          }
          return v
        })

        setVariables(updatedVariables)
        const { groups: updatedGroups, rootVariables: updatedRootVars } =
          groupVariables(updatedVariables)
        setGroups(updatedGroups)
        setRootVariables(updatedRootVars)

        const profile = availableProfiles.find((p) => p.name === profileName)
        setSelectedProfile(profile || null)
        setHasChanges(false)
      } catch (error) {
        console.error("Failed to load profile:", error)
        notify.toast.error("Failed to load profile")
      }
    },
    [
      project.id,
      getProfile,
      allVariables,
      availableProfiles,
      setVariables,
      setGroups,
      setRootVariables,
      setSelectedProfile,
      setHasChanges,
    ]
  )

  /**
   * Handle saving current map to selected profile
   */
  const handleSave = useCallback(async () => {
    if (!selectedProfile) {
      setSaveAsModalOpen(true)
      return
    }

    try {
      const csv = exportMapToCSV(allVariables)
      await saveProfile(project.id, selectedProfile.name, csv)
      notify.toast.success(`Profile "${selectedProfile.name}" saved`)
      setHasChanges(false)
    } catch (error) {
      console.error("Failed to save profile:", error)
      notify.toast.error("Failed to save profile")
    }
  }, [selectedProfile, allVariables, saveProfile, project.id, setHasChanges])

  /**
   * Handle saving as new profile
   */
  const handleSaveAs = useCallback(async () => {
    if (!newProfileName.trim()) {
      notify.toast.error("Please enter a profile name")
      return
    }

    try {
      const csv = exportMapToCSV(allVariables)
      await saveProfile(project.id, newProfileName, csv)

      // Add new profile to list
      const newProfile: ExportMapProfile = {
        name: newProfileName.toLowerCase().replace(/\s+/g, "_"),
        filename: `$exportmap.${newProfileName
          .toLowerCase()
          .replace(/\s+/g, "_")}.csv`,
      }
      setAvailableProfiles([...availableProfiles, newProfile])
      setSelectedProfile(newProfile)

      notify.toast.success(`Profile "${newProfileName}" created`)
      setSaveAsModalOpen(false)
      setNewProfileName("")
      setHasChanges(false)
    } catch (error) {
      console.error("Failed to save profile:", error)
      notify.toast.error("Failed to save profile")
    }
  }, [
    newProfileName,
    allVariables,
    saveProfile,
    project.id,
    availableProfiles,
    setAvailableProfiles,
    setSelectedProfile,
    setHasChanges,
  ])

  /**
   * Handle deleting selected profile
   */
  const handleDelete = useCallback(async () => {
    if (!selectedProfile) return

    try {
      await deleteProfile(project.id, selectedProfile.name)

      // Remove from list
      setAvailableProfiles(
        availableProfiles.filter((p) => p.name !== selectedProfile.name)
      )
      setSelectedProfile(null)

      notify.toast.success(`Profile "${selectedProfile.name}" deleted`)
    } catch (error) {
      console.error("Failed to delete profile:", error)
      notify.toast.error("Failed to delete profile")
    }
  }, [
    selectedProfile,
    deleteProfile,
    project.id,
    availableProfiles,
    setAvailableProfiles,
    setSelectedProfile,
  ])

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-600">Profile:</span>

      <Select
        value={selectedProfile?.name}
        onChange={handleProfileChange}
        loading={isLoadingProfile}
        placeholder="Select profile"
        className="w-48"
        allowClear
        options={availableProfiles.map((p) => ({
          value: p.name,
          label: p.name === "default" ? "Default" : p.name,
        }))}
      />

      <DataQButton
        type="secondary"
        onClick={handleSave}
        isLoading={isSavingProfile}
      >
        <FontAwesomeIcon icon={faFloppyDisk} />
        Save
      </DataQButton>

      <DataQButton type="secondary" onClick={() => setSaveAsModalOpen(true)}>
        <FontAwesomeIcon icon={faPlus} />
        Save As
      </DataQButton>

      {selectedProfile && (
        <Popconfirm
          title="Delete profile"
          description={`Are you sure you want to delete "${selectedProfile.name}"?`}
          onConfirm={handleDelete}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <DataQButton
            type="secondary"
            isLoading={isDeletingProfile}
            className="text-red-500 hover:text-red-600"
          >
            <FontAwesomeIcon icon={faTrash} />
          </DataQButton>
        </Popconfirm>
      )}

      {/* Save As Modal */}
      <Modal
        title="Save As New Profile"
        open={saveAsModalOpen}
        onCancel={() => setSaveAsModalOpen(false)}
        onOk={handleSaveAs}
        confirmLoading={isSavingProfile}
      >
        <div className="py-4">
          <label className="block text-gray-600 mb-2">Profile Name:</label>
          <Input
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            placeholder="Enter profile name"
            autoFocus
          />
          <p className="text-gray-400 text-sm mt-2">
            Profile names are converted to lowercase with underscores.
          </p>
        </div>
      </Modal>
    </div>
  )
}

export default ExportMapProfileSelector
